import { BlobModel, FormFieldChoice, FormFieldType, LoadingStateModel, type FormField, type MatrixValue } from '@/models';
import { type GraphFilter, type SurgeonDashboardData } from '@/models/surgeonDashboard';
import BlobService from '@/services/blobService';
import SurgeonDashboardService from '@/services/SurgeonDashboardService';
import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';


export const useSurgeonDashboardStore = defineStore('surgeon-dashboard', () => {

  const perfusionSummaryData = ref<FormField[]>();
  const perfusionGasesData = ref<FormField[]>();
  const perfusionFlowsData = ref<FormField[]>();
  const perfusionLabsData = ref<FormField[]>();
  const perfusionBileData = ref<FormField[]>();
  const timeSeriesTimepoints = ref<FormFieldChoice[]>();
  const summaryTables = ref<FormField[]>([]);

  const isValidMatrixValue = (col: { id: string; value: unknown }) =>
    col.value !== undefined &&
    col.value !== '' &&
    col.value !== null &&
    col.value !== 'undefined';

  const isDashboardLoading = reactive(new LoadingStateModel({ isLoading: false }));
  const getSurgeonDashboardData = async (caseId: string) => {
    isDashboardLoading.startLoading();
    try {
      const data = await SurgeonDashboardService.getSurgeonDashboardData(caseId);
      if (data) {
        // loading images asynchronously, instead of watiing for all images to load before setting the data
        const imageFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.FILE);
        imageFields.forEach(imageField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === imageField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });
        getFilesMetadata(imageFields);

        let matrixFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.MATRIX);
        // Remove all matrix fields from perfusionSummaryData
        matrixFields.forEach(matrixField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === matrixField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });

        //filter out matrixFields that have no valid values
        matrixFields = matrixFields.filter(summaryData => {
          const values = summaryData.value ? JSON.parse(summaryData.value) as MatrixValue[] : [];
          const finalValues = values.filter((row: MatrixValue) => row.values.some(isValidMatrixValue));
          return finalValues.length > 0;
        });
        summaryTables.value = matrixFields;
      }
    } catch (error) {
      isDashboardLoading.setError(error);
    } finally {
      isDashboardLoading.stopLoadingAndFinish();
    }
  };

  const isSummaryLoading = reactive(new LoadingStateModel({ isLoading: false }));
  const getPerfusionSummaryData = async (caseId: string) => {
    perfusionSummaryData.value = undefined;
    isSummaryLoading.startLoading();
    isGasesLoading.startLoading();
    isFlowsLoading.startLoading();
    isLabsLoading.startLoading();
    try {
      const data = await SurgeonDashboardService.getPerfusionSummaryData(caseId);
      await new Promise(resolve => setTimeout(resolve, 10)); // Simulating network delay for better UX
      if (data) {
        timeSeriesTimepoints.value = data?.timeSeriesTimepoints?.sort(
          (a, b) => b.id - a.id
        ) ?? [];
        // loading images asynchronously, instead of waiting for all images to load before setting the data
        const imageFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.FILE);
        imageFields.forEach(imageField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === imageField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });
        getFilesMetadata(imageFields);

        let matrixFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.MATRIX);
        // Remove all matrix fields from perfusionSummaryData
        matrixFields.forEach(matrixField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === matrixField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });
        perfusionSummaryData.value = data.perfusionSummaryData;

        //filter out matrixFields that have no valid values
        matrixFields = matrixFields.filter(summaryData => {
          const values = summaryData.value ? JSON.parse(summaryData.value) as MatrixValue[] : [];
          const finalValues = values.filter((row: MatrixValue) => row.values.some(isValidMatrixValue));
          return finalValues.length > 0;
        });
        summaryTables.value = matrixFields;
      }
    } catch (error) {
      isSummaryLoading.setError(error);
    } finally {
      isSummaryLoading.stopLoadingAndFinish();
    }
  };


  const isGasesLoading = reactive(new LoadingStateModel({ isLoading: false }));
  const getPerfusionGasesData = async (caseId: string) => {
    isGasesLoading.startLoading();
    perfusionGasesData.value = undefined;
    try {
      const data = await SurgeonDashboardService.getPerfusionGasesData(caseId);
      await new Promise(resolve => setTimeout(resolve, 10)); // Simulating network delay for better UX
      perfusionGasesData.value = data?.gases ?? [];
    } catch (error) {
      isGasesLoading.setError(error);
    } finally {
      isGasesLoading.stopLoadingAndFinish();
    }
  };

  const isFlowsLoading = reactive(new LoadingStateModel({ isLoading: false }));
  const getPerfusionFlowsData = async (caseId: string) => {
    isFlowsLoading.startLoading();
    perfusionFlowsData.value = undefined;
    try {
      const data = await SurgeonDashboardService.getPerfusionFlowsData(caseId);
      await new Promise(resolve => setTimeout(resolve, 10)); // Simulating network delay for better UX
      perfusionFlowsData.value = data?.flows ?? [];
    } catch (error) {
      isFlowsLoading.setError(error);
    } finally {
      isFlowsLoading.stopLoadingAndFinish();
    }
  };

  const isLabsLoading = reactive(new LoadingStateModel({ isLoading: false }));
  const getPerfusionLabsData = async (caseId: string) => {
    isLabsLoading.startLoading();
    perfusionLabsData.value = undefined;
    try {
      const data = await SurgeonDashboardService.getPerfusionLabsData(caseId);
      await new Promise(resolve => setTimeout(resolve, 10)); // Simulating network delay for better UX
      perfusionLabsData.value = data?.labs ?? [];
    } catch (error) {
      isLabsLoading.setError(error);
    } finally {
      isLabsLoading.stopLoadingAndFinish();
    }
  };

  const isBileLoading = reactive(new LoadingStateModel({ isLoading: false }));
  const getPerfusionBileData = async (caseId: string) => {
    isBileLoading.startLoading();
    perfusionBileData.value = undefined;
    try {
      const data = await SurgeonDashboardService.getPerfusionBileData(caseId);
      await new Promise(resolve => setTimeout(resolve, 10)); // Simulating network delay for better UX
      perfusionBileData.value = data?.bile ?? [];
    } catch (error) {
      isBileLoading.setError(error);
    } finally {
      isBileLoading.stopLoadingAndFinish();
    }
  };

  const interactiveDashboardData = ref<SurgeonDashboardData>();

  const getInteractiveSurgeonDashboardData = async (caseId: string, pumpFilters?: GraphFilter[]) => {
    isDashboardLoading.startLoading();
    interactiveDashboardData.value = undefined;
    try {
      let postFilters: Record<string, string> | undefined = undefined;
      if (pumpFilters && pumpFilters.length > 0) {
        postFilters = {};
        pumpFilters?.forEach(filter => {
          postFilters![filter.key] = filter.value;
        });
      }
      const data: SurgeonDashboardData = await SurgeonDashboardService.getInteractiveSurgeonDashboardData(caseId, undefined, postFilters);
      if (data) {
        // loading images asynchronously, instead of watiing for all images to load before setting the data
        const imageFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.FILE);
        imageFields.forEach(imageField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === imageField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });
        getFilesMetadata(imageFields);

        let matrixFields = data.perfusionSummaryData.filter(summaryData => summaryData.type == FormFieldType.MATRIX);
        // Remove all matrix fields from perfusionSummaryData
        matrixFields.forEach(matrixField => {
          const index = data.perfusionSummaryData.findIndex(summaryData => summaryData.variableName === matrixField.variableName);
          if (index > -1) {
            data.perfusionSummaryData.splice(index, 1);
          }
        });

        //filter out matrixFields that have no valid values
        matrixFields = matrixFields.filter(summaryData => {
          const values = summaryData.value ? JSON.parse(summaryData.value) as MatrixValue[] : [];
          const finalValues = values.filter((row: MatrixValue) => row.values.some(isValidMatrixValue));
          return finalValues.length > 0;
        });
        summaryTables.value = matrixFields;
      }
      interactiveDashboardData.value = data;
    } catch (error) {
      isDashboardLoading.setError(error);
    } finally {
      isDashboardLoading.stopLoadingAndFinish();
    }
  };

  const getInteractiveVariableData = async (caseId: string, field?: FormField, postFilters?: Record<string, string>) => {
    try {
      return await SurgeonDashboardService.getInteractiveSurgeonDashboardData(caseId, field?.variableName, postFilters);
    } catch (error) {
      isDashboardLoading.setError(error);
    }
  };

  const summaryLiverImages = ref<FormField[]>([]);

  const getFilesMetadata = async (imageFields: FormField[]) => {
    for (const field of imageFields) {
      const files: BlobModel[] = [];
      if (field.fileIds) {
        // If fileIds is a string, parse it to an array
        for (const fileId of field.fileIds) {
          try {
            const file = await BlobService.getBlobMetadata(fileId);
            if (file) {
              files.push(file);
            }
          } catch (error) { /* empty */ }
        }
      } else if (field.images) {
        for (const image of field.images) {
          try {
            const file = await BlobService.getBlobMetadata(image.value);
            if (file) {
              file.label = image.label;
              files.push(file);
            }
          } catch (error) { /* empty */ }
        }
      }
      field.files = files;
    }
    summaryLiverImages.value = imageFields;
  };

  const loadFullResolutionImages = async (variableName: string) => {
    const imageField = summaryLiverImages.value.find(field => field.variableName === variableName);
    if (imageField) {
      for (const image of imageField.files ?? []) {
        image.downloadedBlob = await BlobService.downloadBlob(image.identifier, false, image.name, true);
      }
    }
  };

  const responsiveOptions = ref([
    {
      breakpoint: '1500px',
      numVisible: 5
    },
    {
      breakpoint: '1024px',
      numVisible: 3
    },
    {
      breakpoint: '768px',
      numVisible: 2
    },
    {
      breakpoint: '560px',
      numVisible: 1
    }
  ]);

  return {
    getSurgeonDashboardData,
    getInteractiveSurgeonDashboardData,
    getInteractiveVariableData,
    getPerfusionSummaryData,
    getPerfusionGasesData,
    getPerfusionFlowsData,
    getPerfusionLabsData,
    getPerfusionBileData,
    perfusionSummaryData,
    perfusionGasesData,
    perfusionFlowsData,
    perfusionLabsData,
    perfusionBileData,
    timeSeriesTimepoints,
    interactiveDashboardData,
    summaryLiverImages,
    summaryTables,
    responsiveOptions,
    loadFullResolutionImages,
    isDashboardLoading,
    isSummaryLoading,
    isGasesLoading,
    isFlowsLoading,
    isLabsLoading,
    isBileLoading
  };
});
